import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getApiUrl } from '../config/api.config';
import Logo from '../components/Logo';
import StartupPortfolioCard from '../components/StartupPortfolioCard';
import AlertDialog from '../components/AlertDialog';
import FileTraceModal from '../components/FileTraceModal';
import CreateInvestorModal from '../components/CreateInvestorModal';
import CreateNetworkUserModal from '../components/CreateNetworkUserModal';
import ChangePasswordModal from '../components/ChangePasswordModal';
import FileUpload from '../components/FileUpload';
import InvestmentSectionTabs from '../components/investments/InvestmentSectionTabs';
import { fileAPI, networkUserAPI, startupInvestmentApplicationAPI } from '../lib/api';
import { portfolioInvestmentForCard } from '../lib/investorNetworkPortfolio';

const DASHBOARD_TABS = ['portfolio', 'startups', 'applied-startups', 'investors', 'users', 'documents', 'activity'];
const LINK_STARTUP_SECTION_TABS = [
    { id: 'overview', label: 'Overview' },
    { id: 'share-details', label: 'Share Details' },
    { id: 'tranche-1', label: 'Tranche 1' },
    { id: 'tranche-2', label: 'Tranche 2' },
    { id: 'post-completion', label: 'Tranche 3' }
];
const LINK_STARTUP_SECTION_COLUMNS = {
    overview: [
        { label: 'Amount', networkKey: 'networkAmount', stateKey: 'amounts', step: '1' },
        { label: 'Post holding %', networkKey: 'networkEquityPercentage', stateKey: 'equityPercentages', step: '0.01' },
        { label: 'No. of CCPS', networkKey: 'networkShareCount', stateKey: 'shareCounts', step: '1' },
        { label: 'Issue price', networkKey: 'networkShareValue', stateKey: 'shareValues', step: '0.01' },
        { label: 'Status', placeholder: 'ACTIVE' }
    ],
    'share-details': [
        { label: 'Partly Paid Amount', networkKey: 'networkPartlyPaid', stateKey: 'partlyPaidAmounts', step: '0.01' },
        { label: 'Nett Subscription Amount', networkKey: 'networkNettSubscription', stateKey: 'nettSubscriptionAmounts', step: '0.01' },
        { label: 'Entry share equity', networkKey: 'networkEntryShareEquity', stateKey: 'entryShareEquities', step: '0.01' }
    ],
    'tranche-1': [
        { label: 'Tranche-1 Sub. Amount', networkKey: 'networkTranche1Amount', stateKey: 'tranche1Amounts', step: '0.01' },
        { label: 'Tranche-1 No. of CCPS', networkKey: 'networkTranche1Ccps', stateKey: 'tranche1CcpsCount', step: '1' },
        { label: 'Tranche-1 Holding %', networkKey: 'networkTranche1Holding', stateKey: 'tranche1HoldingPct', step: '0.01' }
    ],
    'tranche-2': [
        { label: 'Tranche-2 Sub. Amount', networkKey: 'networkTranche2Amount', stateKey: 'tranche2Amounts', step: '0.01' },
        { label: 'Tranche-2 No. of CCPS', networkKey: 'networkTranche2Ccps', stateKey: 'tranche2CcpsCount', step: '1' },
        { label: 'Tranche-2 Holding %', networkKey: 'networkTranche2Holding', stateKey: 'tranche2HoldingPct', step: '0.01' }
    ],
    'post-completion': [
        { label: 'Tranche-3 Sub. Amount', networkKey: 'networkPostAmount', stateKey: 'postAmounts', step: '0.01' },
        { label: 'Tranche-3 No. of CCPS', networkKey: 'networkPostCcps', stateKey: 'postCcpsCount', step: '1' },
        { label: 'Tranche-3 Holding %', networkKey: 'networkPostHolding', stateKey: 'postHoldingPct', step: '0.01' }
    ]
};

const InvestorNetworkDashboard = () => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const { tenantId, tab: tabParam } = useParams();
    const tenantPath = tenantId || user?.tenantIdentifier || 'default';
    const activeTab = DASHBOARD_TABS.includes(tabParam) ? tabParam : 'portfolio';
    const [loading, setLoading] = useState(true);
    const [showProfileMenu, setShowProfileMenu] = useState(false);
    const profileMenuRef = useRef(null);

    // Data states
    const [investors, setInvestors] = useState([]);
    const [networkUsers, setNetworkUsers] = useState([]);
    const [startups, setStartups] = useState([]);
    const [investments, setInvestments] = useState([]);
    /** { [startupUserId]: { totalAmount, currency } } from API — linked portfolio + network investors only */
    const [networkStartupTotals, setNetworkStartupTotals] = useState({});
    /** Startup user ids linked to this network (for client fallback); null = do not filter fallback */
    const [networkLinkedStartupIds, setNetworkLinkedStartupIds] = useState(null);
    const [files, setFiles] = useState([]);
    const [linkedStartupActivity, setLinkedStartupActivity] = useState([]);
    const [startupApplications, setStartupApplications] = useState([]);
    const [acceptStartupRequests, setAcceptStartupRequests] = useState(true);
    const [stats, setStats] = useState({
        totalInvestments: 0,
        totalInvested: 0,
        activeStartups: 0,
        teamMembers: 0,
        totalNetworkUsers: 0,
        totalDocuments: 0
    });

    const [alert, setAlert] = useState({ isOpen: false, title: '', message: '', type: 'info' });
    const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [showFileModal, setShowFileModal] = useState(false);
    const [traceFile, setTraceFile] = useState(null);
    const [showTraceModal, setShowTraceModal] = useState(false);
    const [showAddInvestorModal, setShowAddInvestorModal] = useState(false);
    const [showAddUserModal, setShowAddUserModal] = useState(false);
    const [editingNetworkUser, setEditingNetworkUser] = useState(null);
    // Search investor by email (investors tab)
    const [investorSearchEmail, setInvestorSearchEmail] = useState('');
    const [investorSearchResult, setInvestorSearchResult] = useState(null);
    const [investorSearchLoading, setInvestorSearchLoading] = useState(false);
    const [pendingCreateEmail, setPendingCreateEmail] = useState('');
    const [investorSearchExpanded, setInvestorSearchExpanded] = useState(false);
    const [selectedInvestorForStartups, setSelectedInvestorForStartups] = useState(null);

    // Link startup with investments modal (startups tab)
    const [linkStartupWithInvestmentsModal, setLinkStartupWithInvestmentsModal] = useState(null);
    const [linkInvestmentsForm, setLinkInvestmentsForm] = useState({
        notReadyToInvestYet: false,
        investmentStage: 'SEED',
        currency: 'INR',
        investmentType: 'EQUITY',
        totalAmount: '',
        networkAmount: '',
        networkEquityPercentage: '',
        networkShareValue: '',
        networkShareCount: '',
        networkEntryShareEquity: '',
        networkPartlyPaid: '',
        networkNettSubscription: '',
        networkTranche1Amount: '',
        networkTranche1Ccps: '',
        networkTranche1Holding: '',
        networkTranche2Amount: '',
        networkTranche2Ccps: '',
        networkTranche2Holding: '',
        networkPostAmount: '',
        networkPostCcps: '',
        networkPostHolding: '',
        shareValues: {},
        shareCounts: {},
        equityPercentages: {},
        amounts: {},
        entryShareEquities: {},
        partlyPaidAmounts: {},
        nettSubscriptionAmounts: {},
        tranche1Amounts: {},
        tranche1CcpsCount: {},
        tranche1HoldingPct: {},
        tranche2Amounts: {},
        tranche2CcpsCount: {},
        tranche2HoldingPct: {},
        postAmounts: {},
        postCcpsCount: {},
        postHoldingPct: {}
    });
    const [linkStartupSubmitting, setLinkStartupSubmitting] = useState(false);
    const [activeLinkStartupSection, setActiveLinkStartupSection] = useState(LINK_STARTUP_SECTION_TABS[0].id);
    const currentLinkStartupColumns = LINK_STARTUP_SECTION_COLUMNS[activeLinkStartupSection] || LINK_STARTUP_SECTION_COLUMNS.overview;

    // Link startup by email or name (startups tab)
    const [startupSearchQuery, setStartupSearchQuery] = useState('');
    const [startupSearchResult, setStartupSearchResult] = useState(null);
    const [startupSearchLoading, setStartupSearchLoading] = useState(false);

    // Network logo (uploaded by INVESTOR_NETWORK)
    const [networkLogoUrl, setNetworkLogoUrl] = useState(null);
    const [networkLogoLoading, setNetworkLogoLoading] = useState(false);
    const [logoUploading, setLogoUploading] = useState(false);
    const logoInputRef = useRef(null);

    // Sidebar: collapsed (icons only) by default; user expands via the toggle only
    const [sidebarExpanded, setSidebarExpanded] = useState(false);
    const sidebarOpen = sidebarExpanded;

    // Admin mode: select an investor network to view/manage
    const isAdminMode = user?.role === 'ADMIN' || user?.role === 'TENANT_ADMIN';
    const isManagementUser = user?.role === 'MANAGEMENT';
    const [investorNetworks, setInvestorNetworks] = useState([]);
    const [selectedNetworkId, setSelectedNetworkId] = useState(null);
    const effectiveNetworkId = isAdminMode ? selectedNetworkId : (isManagementUser ? user?.investorNetworkId : user?.id);

    // Redirect invalid tab to portfolio; redirect investors tab to dedicated page
    useEffect(() => {
        if (tabParam === 'investors') {
            navigate(`/${tenantPath}/investor-network/investors`, { replace: true });
            return;
        }
        if (tabParam && !DASHBOARD_TABS.includes(tabParam)) {
            navigate(`/${tenantPath}/investor-network`, { replace: true });
        }
    }, [tabParam, tenantPath, navigate]);

    const navigateToTab = (tab) => {
        if (tab === 'investors') {
            navigate(`/${tenantPath}/investor-network/investors`);
            return;
        }
        if (tab === 'portfolio') {
            navigate(`/${tenantPath}/investor-network`);
            return;
        }
        navigate(`/${tenantPath}/investor-network/${tab}`);
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
                setShowProfileMenu(false);
            }
        };

        if (showProfileMenu) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showProfileMenu]);

    useEffect(() => {
        if (isAdminMode) {
            fetch(getApiUrl('admin/users/investor-networks'), { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } })
                .then(r => r.ok ? r.json() : [])
                .then(networks => {
                    setInvestorNetworks(networks || []);
                    if (networks?.length && !selectedNetworkId) setSelectedNetworkId(networks[0].id);
                })
                .catch(() => setInvestorNetworks([]));
        }
    }, [isAdminMode]);

    useEffect(() => {
        if (!user?.id) return;
        if (isAdminMode && !effectiveNetworkId) return;
        // MANAGEMENT uses same dashboard as INVESTOR_NETWORK (effectiveNetworkId = their network)
        loadDashboardData();
    }, [user?.id, isAdminMode, effectiveNetworkId]);

    const loadDashboardData = async () => {
        setLoading(true);
        try {
            const investorsData = await loadInvestors();
            await Promise.all([
                loadNetworkUsers(),
                loadPortfolio(investorsData),
                loadFiles(),
                loadLinkedStartupActivity(),
                loadStartupApplications(),
                loadNetworkRequestPreference()
            ]);
        } catch (error) {
            console.error('Failed to load dashboard data:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadStartupApplications = async () => {
        try {
            const list = await startupInvestmentApplicationAPI.getInbox();
            setStartupApplications(Array.isArray(list) ? list : []);
        } catch (error) {
            console.error('Failed to load startup applications:', error);
            setStartupApplications([]);
        }
    };

    const loadNetworkRequestPreference = async () => {
        try {
            const response = await fetch(getApiUrl('users/my-network/profile'), {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            if (response.ok) {
                const data = await response.json();
                setAcceptStartupRequests(data.acceptStartupRequests !== false);
            }
        } catch (error) {
            console.error('Failed to load network profile preference:', error);
        }
    };

    const loadInvestors = async () => {
        try {
            const headers = { 'Authorization': `Bearer ${localStorage.getItem('token')}` };
            if (isAdminMode && effectiveNetworkId) {
                const response = await fetch(getApiUrl(`admin/users/investors?networkId=${effectiveNetworkId}`), { headers });
                if (response.ok) {
                    const list = await response.json();
                    setInvestors(list || []);
                    setStats(prev => ({ ...prev, teamMembers: (list || []).length }));
                    return list || [];
                }
            } else if (!isAdminMode) {
                const response = await fetch(getApiUrl('users/my-network/investors'), { headers });
                if (response.ok) {
                    const data = await response.json();
                    const list = data || [];
                    setInvestors(list);
                    setStats(prev => ({ ...prev, teamMembers: list.length }));
                    return list;
                }
            }
        } catch (error) {
            console.error('Failed to load investors:', error);
        }
        return [];
    };

    const searchInvestorByEmail = async () => {
        const email = (investorSearchEmail || '').trim();
        if (!email) {
            setAlert({ isOpen: true, title: 'Validation', message: 'Please enter an email address.', type: 'info' });
            return;
        }
        setInvestorSearchLoading(true);
        setInvestorSearchResult(null);
        try {
            const response = await fetch(getApiUrl(`users/my-network/search-investor?email=${encodeURIComponent(email)}`), {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            const data = await response.json();
            if (response.ok) {
                setInvestorSearchResult(data);
            } else {
                setAlert({ isOpen: true, title: 'Search failed', message: data.error || data.message || 'Could not search investor.', type: 'error' });
            }
        } catch (error) {
            console.error('Investor search failed:', error);
            setAlert({ isOpen: true, title: 'Error', message: 'Search failed. Please try again.', type: 'error' });
        } finally {
            setInvestorSearchLoading(false);
        }
    };

    const linkInvestorToNetwork = async (investorId) => {
        try {
            const response = await fetch(getApiUrl('users/my-network/link-investor'), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({ investorId })
            });
            const data = await response.json();
            if (response.ok) {
                setInvestorSearchResult(null);
                setInvestorSearchEmail('');
                loadDashboardData();
                setAlert({ isOpen: true, title: 'Success', message: data.message || 'Investor linked to your network.', type: 'success' });
            } else {
                setAlert({ isOpen: true, title: 'Error', message: data.error || data.message || 'Failed to link investor.', type: 'error' });
            }
        } catch (error) {
            console.error('Link investor failed:', error);
            setAlert({ isOpen: true, title: 'Error', message: 'Failed to link investor. Please try again.', type: 'error' });
        }
    };

    const searchStartupByEmailOrName = async () => {
        const query = (startupSearchQuery || '').trim();
        if (!query) {
            setAlert({ isOpen: true, title: 'Validation', message: 'Enter an email or startup name to search.', type: 'info' });
            return;
        }
        setStartupSearchLoading(true);
        setStartupSearchResult(null);
        try {
            const isEmail = query.includes('@');
            const params = new URLSearchParams();
            if (isEmail) params.set('email', query);
            else params.set('name', query);
            const response = await fetch(getApiUrl(`users/my-network/search-startup?${params}`), {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            const data = await response.json();
            if (response.ok) {
                setStartupSearchResult(data);
            } else {
                setAlert({ isOpen: true, title: 'Search failed', message: data.error || data.message || 'Could not search startup.', type: 'error' });
            }
        } catch (error) {
            console.error('Startup search failed:', error);
            setAlert({ isOpen: true, title: 'Error', message: 'Search failed. Please try again.', type: 'error' });
        } finally {
            setStartupSearchLoading(false);
        }
    };

    const linkStartupToNetwork = async (startupId) => {
        try {
            const response = await fetch(getApiUrl('users/my-network/link-startup'), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({ startupId })
            });
            const data = await response.json();
            if (response.ok) {
                setStartupSearchResult(null);
                setStartupSearchQuery('');
                loadDashboardData();
                setAlert({ isOpen: true, title: 'Success', message: data.message || 'Startup linked to your network.', type: 'success' });
            } else {
                setAlert({ isOpen: true, title: 'Error', message: data.error || data.message || 'Failed to link startup.', type: 'error' });
            }
        } catch (error) {
            console.error('Link startup failed:', error);
            setAlert({ isOpen: true, title: 'Error', message: 'Failed to link startup. Please try again.', type: 'error' });
        }
    };

    const openLinkStartupWithInvestmentsModal = (startup) => {
        setLinkStartupWithInvestmentsModal({ startup: { id: startup.id, name: startup.organizationName || startup.name, email: startup.email } });
        setActiveLinkStartupSection(LINK_STARTUP_SECTION_TABS[0].id);
        setLinkInvestmentsForm({
            notReadyToInvestYet: false,
            investmentStage: 'SEED',
            currency: 'INR',
            investmentType: 'EQUITY',
            totalAmount: '',
            networkAmount: '',
            networkEquityPercentage: '',
            networkShareValue: '',
            networkShareCount: '',
            networkEntryShareEquity: '',
            networkPartlyPaid: '',
            networkNettSubscription: '',
            networkTranche1Amount: '',
            networkTranche1Ccps: '',
            networkTranche1Holding: '',
            networkTranche2Amount: '',
            networkTranche2Ccps: '',
            networkTranche2Holding: '',
            networkPostAmount: '',
            networkPostCcps: '',
            networkPostHolding: '',
            shareValues: {},
            shareCounts: {},
            equityPercentages: {},
            amounts: {},
            entryShareEquities: {},
            partlyPaidAmounts: {},
            nettSubscriptionAmounts: {},
            tranche1Amounts: {},
            tranche1CcpsCount: {},
            tranche1HoldingPct: {},
            tranche2Amounts: {},
            tranche2CcpsCount: {},
            tranche2HoldingPct: {},
            postAmounts: {},
            postCcpsCount: {},
            postHoldingPct: {}
        });
    };

    const linkStartupWithInvestmentsToNetwork = async () => {
        if (!linkStartupWithInvestmentsModal?.startup?.id) return;
        const startupId = linkStartupWithInvestmentsModal.startup.id;
        const parseOptF = (v) => (v !== undefined && String(v).trim() !== '' ? parseFloat(v) : undefined);
        const parseOptI = (v) => (v !== undefined && String(v).trim() !== '' ? parseInt(v, 10) : undefined);
        const investments = investors
            .map((inv) => ({
                investorId: inv.id,
                amount: parseFloat(linkInvestmentsForm.amounts[inv.id]) || 0,
                equityPercentage: parseOptF(linkInvestmentsForm.equityPercentages[inv.id]),
                entrySharePrice: parseOptF(linkInvestmentsForm.shareValues[inv.id]),
                entryShareEquity: parseOptF(linkInvestmentsForm.entryShareEquities[inv.id]),
                numberOfShares: parseOptI(linkInvestmentsForm.shareCounts[inv.id]),
                partlyPaidAmount: parseOptF(linkInvestmentsForm.partlyPaidAmounts[inv.id]),
                nettSubscriptionAmount: parseOptF(linkInvestmentsForm.nettSubscriptionAmounts[inv.id]),
                tranche1SubscriptionAmount: parseOptF(linkInvestmentsForm.tranche1Amounts[inv.id]),
                tranche1NoCcps: parseOptI(linkInvestmentsForm.tranche1CcpsCount[inv.id]),
                tranche1HoldingPercentage: parseOptF(linkInvestmentsForm.tranche1HoldingPct[inv.id]),
                tranche2SubscriptionAmount: parseOptF(linkInvestmentsForm.tranche2Amounts[inv.id]),
                tranche2NoCcps: parseOptI(linkInvestmentsForm.tranche2CcpsCount[inv.id]),
                tranche2HoldingPercentage: parseOptF(linkInvestmentsForm.tranche2HoldingPct[inv.id]),
                postCompletionSubscriptionAmount: parseOptF(linkInvestmentsForm.postAmounts[inv.id]),
                postCompletionNoCcps: parseOptI(linkInvestmentsForm.postCcpsCount[inv.id]),
                postCompletionHoldingPercentage: parseOptF(linkInvestmentsForm.postHoldingPct[inv.id]),
            }))
            .filter((e) => e.amount > 0);
        const networkAmount = parseFloat(linkInvestmentsForm.networkAmount) || 0;
        if (networkAmount > 0 && effectiveNetworkId) {
            investments.unshift({
                investorId: effectiveNetworkId,
                amount: networkAmount,
                equityPercentage: parseOptF(linkInvestmentsForm.networkEquityPercentage),
                entrySharePrice: parseOptF(linkInvestmentsForm.networkShareValue),
                entryShareEquity: parseOptF(linkInvestmentsForm.networkEntryShareEquity),
                numberOfShares: parseOptI(linkInvestmentsForm.networkShareCount),
                partlyPaidAmount: parseOptF(linkInvestmentsForm.networkPartlyPaid),
                nettSubscriptionAmount: parseOptF(linkInvestmentsForm.networkNettSubscription),
                tranche1SubscriptionAmount: parseOptF(linkInvestmentsForm.networkTranche1Amount),
                tranche1NoCcps: parseOptI(linkInvestmentsForm.networkTranche1Ccps),
                tranche1HoldingPercentage: parseOptF(linkInvestmentsForm.networkTranche1Holding),
                tranche2SubscriptionAmount: parseOptF(linkInvestmentsForm.networkTranche2Amount),
                tranche2NoCcps: parseOptI(linkInvestmentsForm.networkTranche2Ccps),
                tranche2HoldingPercentage: parseOptF(linkInvestmentsForm.networkTranche2Holding),
                postCompletionSubscriptionAmount: parseOptF(linkInvestmentsForm.networkPostAmount),
                postCompletionNoCcps: parseOptI(linkInvestmentsForm.networkPostCcps),
                postCompletionHoldingPercentage: parseOptF(linkInvestmentsForm.networkPostHolding),
            });
        }
        const totalAmount = parseFloat(linkInvestmentsForm.totalAmount);
        const allocatedAmount = investments.reduce((sum, entry) => sum + entry.amount, 0);
        const skipInvestments = Boolean(linkInvestmentsForm.notReadyToInvestYet);
        if (skipInvestments) {
            setLinkStartupSubmitting(true);
            try {
                const response = await fetch(getApiUrl('users/my-network/link-startup'), {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    },
                    body: JSON.stringify({ startupId })
                });
                const data = await response.json();
                if (response.ok) {
                    setLinkStartupWithInvestmentsModal(null);
                    setStartupSearchResult(null);
                    setStartupSearchQuery('');
                    loadDashboardData();
                    setAlert({ isOpen: true, title: 'Success', message: data.message || 'Startup linked. Investments can be recorded later when investors are ready.', type: 'success' });
                } else {
                    setAlert({ isOpen: true, title: 'Error', message: data.error || data.message || 'Failed to link startup.', type: 'error' });
                }
            } catch (error) {
                console.error('Link startup failed:', error);
                setAlert({ isOpen: true, title: 'Error', message: 'Failed to link startup. Please try again.', type: 'error' });
            } finally {
                setLinkStartupSubmitting(false);
            }
            return;
        }
        if (investments.length === 0) {
            setAlert({ isOpen: true, title: 'Validation', message: 'Select at least one investor and enter an amount.', type: 'info' });
            return;
        }
        if (isNaN(totalAmount) || totalAmount <= 0) {
            setAlert({ isOpen: true, title: 'Validation', message: 'Enter the full total investment amount.', type: 'info' });
            return;
        }
        if (Math.abs(allocatedAmount - totalAmount) > 0.01) {
            setAlert({
                isOpen: true,
                title: 'Validation',
                message: `Allocated amount (${allocatedAmount.toLocaleString('en-IN')}) must match total amount (${totalAmount.toLocaleString('en-IN')}).`,
                type: 'info'
            });
            return;
        }
        setLinkStartupSubmitting(true);
        try {
            const response = await fetch(getApiUrl('users/my-network/link-startup-with-investments'), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({
                    startupId,
                    investmentStage: linkInvestmentsForm.investmentStage,
                    currency: linkInvestmentsForm.currency,
                    investmentType: linkInvestmentsForm.investmentType,
                    totalAmount,
                    investments
                })
            });
            const data = await response.json();
            if (response.ok) {
                setLinkStartupWithInvestmentsModal(null);
                setStartupSearchResult(null);
                setStartupSearchQuery('');
                loadDashboardData();
                setAlert({ isOpen: true, title: 'Success', message: data.message || 'Startup linked and investments recorded.', type: 'success' });
            } else {
                setAlert({ isOpen: true, title: 'Error', message: data.error || data.message || 'Failed to link startup.', type: 'error' });
            }
        } catch (error) {
            console.error('Link startup with investments failed:', error);
            setAlert({ isOpen: true, title: 'Error', message: 'Failed to link startup. Please try again.', type: 'error' });
        } finally {
            setLinkStartupSubmitting(false);
        }
    };

    const loadNetworkUsers = async () => {
        try {
            if (isAdminMode && effectiveNetworkId) {
                const data = await networkUserAPI.getNetworkUsers(effectiveNetworkId);
                const list = data || [];
                setNetworkUsers(list);
                setStats(prev => ({ ...prev, totalNetworkUsers: list.length }));
                return;
            }
            if (!isAdminMode) {
                const response = await fetch(getApiUrl('users/my-network/users'), {
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                });
                if (response.ok) {
                    const data = await response.json();
                    setNetworkUsers(data || []);
                    setStats(prev => ({ ...prev, totalNetworkUsers: data?.length || 0 }));
                }
            }
        } catch (error) {
            console.error('Failed to load network users:', error);
        }
    };

    const loadPortfolio = async (investorsList) => {
        const list = investorsList ?? investors;
        try {
            const allInvestments = [];
            const startupMap = new Map();
            const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

            let totalsByStartupId = {};
            let linkedIdsForFallback = null;

            if (!isAdminMode) {
                const [invRes, linkedRes, totalsRes] = await Promise.all([
                    fetch(getApiUrl('users/my-network/investments'), { headers }),
                    fetch(getApiUrl('users/my-network/linked-startups'), { headers }),
                    fetch(getApiUrl('users/my-network/investment-totals-by-startup'), { headers }),
                ]);
                if (invRes.ok) {
                    const data = await invRes.json();
                    (data || []).forEach((inv) => allInvestments.push(inv));
                }
                if (linkedRes.ok) {
                    const linked = await linkedRes.json();
                    linkedIdsForFallback = (linked || []).map((s) => Number(s.id));
                    (linked || []).forEach((s) => startupMap.set(s.id, s));
                }
                if (totalsRes.ok) {
                    const totalsList = await totalsRes.json();
                    totalsByStartupId = {};
                    (totalsList || []).forEach((row) => {
                        if (row?.startupId == null) return;
                        totalsByStartupId[Number(row.startupId)] = {
                            totalAmount: row.totalAmount,
                            currency: row.currency || 'INR',
                        };
                    });
                }
                (allInvestments || []).forEach((inv) => {
                    if (inv.startupId != null && !startupMap.has(inv.startupId)) {
                        startupMap.set(inv.startupId, {
                            id: inv.startupId,
                            organizationName: inv.startupName,
                            name: inv.startupName,
                            email: inv.startupEmail,
                        });
                    }
                });
            } else {
                if (!list.length) {
                    setStartups([]);
                    setInvestments([]);
                    setNetworkStartupTotals({});
                    setNetworkLinkedStartupIds(null);
                    return;
                }
                for (const investor of list) {
                    try {
                        const [portfolioRes, investmentsRes] = await Promise.all([
                            fetch(getApiUrl(`investments/investor/${investor.id}/portfolio`), { headers }),
                            fetch(getApiUrl(`investments/investor/${investor.id}`), { headers }),
                        ]);

                        if (portfolioRes.ok) {
                            const portfolio = await portfolioRes.json();
                            portfolio.forEach((startup) => startupMap.set(startup.id, startup));
                        }

                        if (investmentsRes.ok) {
                            const invData = await investmentsRes.json();
                            (invData || []).forEach((inv) => allInvestments.push({ ...inv, investorId: investor.id }));
                        }
                    } catch (err) {
                        console.error(`Failed to load data for investor ${investor.id}:`, err);
                    }
                }

                if (effectiveNetworkId) {
                    try {
                        const [netPort, netInv] = await Promise.all([
                            fetch(getApiUrl(`investments/investor/${effectiveNetworkId}/portfolio`), { headers }),
                            fetch(getApiUrl(`investments/investor/${effectiveNetworkId}`), { headers }),
                        ]);
                        if (netPort.ok) {
                            const portfolio = await netPort.json();
                            (portfolio || []).forEach((startup) => startupMap.set(startup.id, startup));
                        }
                        if (netInv.ok) {
                            const invData = await netInv.json();
                            (invData || []).forEach((inv) => allInvestments.push(inv));
                        }
                    } catch (err) {
                        console.error('Failed to load syndicate account investments for network:', err);
                    }
                }

                try {
                    const linkedRes = await fetch(getApiUrl('users/my-network/linked-startups'), { headers });
                    if (linkedRes.ok) {
                        const linked = await linkedRes.json();
                        linkedIdsForFallback = (linked || []).map((s) => Number(s.id));
                        (linked || []).forEach((s) => startupMap.set(s.id, s));
                    }
                } catch (err) {
                    console.error('Failed to load linked startups:', err);
                }

                (allInvestments || []).forEach((inv) => {
                    if (inv.startupId != null && !startupMap.has(inv.startupId)) {
                        startupMap.set(inv.startupId, {
                            id: inv.startupId,
                            organizationName: inv.startupName,
                            name: inv.startupName,
                            email: inv.startupEmail,
                        });
                    }
                });

                if (effectiveNetworkId) {
                    try {
                        const totalsRes = await fetch(
                            getApiUrl(`admin/investor-networks/${effectiveNetworkId}/investment-totals-by-startup`),
                            { headers },
                        );
                        if (totalsRes.ok) {
                            const totalsList = await totalsRes.json();
                            totalsByStartupId = {};
                            (totalsList || []).forEach((row) => {
                                if (row?.startupId == null) return;
                                totalsByStartupId[Number(row.startupId)] = {
                                    totalAmount: row.totalAmount,
                                    currency: row.currency || 'INR',
                                };
                            });
                        }
                    } catch (err) {
                        console.error('Failed to load admin network investment totals:', err);
                    }
                }
            }

            setStartups(Array.from(startupMap.values()));
            setInvestments(allInvestments);
            setNetworkStartupTotals(totalsByStartupId);
            setNetworkLinkedStartupIds(linkedIdsForFallback);

            // Calculate stats
            const totalInvested = allInvestments.reduce((sum, inv) => sum + (inv.investmentAmount || 0), 0);
            const activeStartups = allInvestments.filter(inv => inv.status === 'ACTIVE').length;

            setStats(prev => ({
                ...prev,
                totalInvestments: allInvestments.length,
                totalInvested,
                activeStartups: startupMap.size
            }));
        } catch (error) {
            console.error('Failed to load portfolio:', error);
        }
    };

    const loadFiles = async () => {
        try {
            // Fetch all accessible files including Public Admin files and Network files
            const response = await fetch(getApiUrl('files'), {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            if (response.ok) {
                const data = await response.json();
                setFiles(data || []);
                setStats(prev => ({ ...prev, totalDocuments: data?.length || 0 }));
            }
        } catch (error) {
            console.error('Failed to load files:', error);
        }
    };

    const loadLinkedStartupActivity = async () => {
        try {
            const response = await fetch(getApiUrl('users/my-network/linked-startups/activity'), {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            if (response.ok) {
                const data = await response.json();
                setLinkedStartupActivity(Array.isArray(data) ? data : []);
            } else {
                setLinkedStartupActivity([]);
            }
        } catch (error) {
            console.error('Failed to load linked startup activity:', error);
            setLinkedStartupActivity([]);
        }
    };

    const formatCurrency = (amount) => {
        if (!amount) return '0';
        if (amount >= 10000000) {
            return `${(amount / 10000000).toFixed(2)} Cr`;
        } else if (amount >= 100000) {
            return `${(amount / 100000).toFixed(2)} L`;
        }
        return amount.toLocaleString('en-IN');
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleString('en-IN', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getFileIcon = (contentType) => {
        if (!contentType) return 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z';

        if (contentType.includes('pdf')) return 'M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z';
        if (contentType.includes('word') || contentType.includes('document')) return 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z';
        if (contentType.includes('excel') || contentType.includes('spreadsheet')) return 'M9 17v1a1 1 0 001 1h4a1 1 0 001-1v-1m3-2V8a2 2 0 00-2-2H9a2 2 0 00-2 2v7m3-2h6';
        if (contentType.includes('powerpoint') || contentType.includes('presentation')) return 'M7 4v16M17 4v16M3 8h4m10 0h4M3 16h4m10 0h4';
        if (contentType.includes('image')) return 'M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z';
        return 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z';
    };

    const previewFile = async (file) => {
        try {
            const response = await fileAPI.getFilePreview(file.id);
            window.open(response.previewUrl, '_blank');
        } catch (err) {
            setAlert({ isOpen: true, title: 'Error', message: 'Failed to preview file', type: 'error' });
        }
    };

    const downloadFile = async (file) => {
        try {
            const response = await fileAPI.getFileDownload(file.id);
            const blob = new Blob([response.data], { type: response.headers['content-type'] });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;

            let filename = file.originalName || 'download';
            const contentDisposition = response.headers['content-disposition'];
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
                if (filenameMatch && filenameMatch.length === 2) {
                    filename = filenameMatch[1];
                }
            }

            link.download = filename;
            document.body.appendChild(link);
            link.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(link);
        } catch (err) {
            console.error('Download error:', err);
            setAlert({ isOpen: true, title: 'Error', message: 'Failed to download file', type: 'error' });
        }
    };

    const getFileMetadata = async (file) => {
        try {
            const response = await fileAPI.getFileMetadata(file.id);
            setSelectedFile({ ...file, ...response });
            setShowFileModal(true);
        } catch (err) {
            setAlert({ isOpen: true, title: 'Error', message: 'Failed to get file metadata', type: 'error' });
        }
    };

    const loadNetworkLogo = async () => {
        if (!user?.id) return;
        setNetworkLogoLoading(true);
        setNetworkLogoUrl((prev) => {
            if (prev && prev.startsWith('blob:')) window.URL.revokeObjectURL(prev);
            return null;
        });
        try {
            const list = await fileAPI.getFilesByModule('INVESTOR_NETWORK', user.id);
            const logoFile = (list || []).find(
                (f) => (f.documentType || f.fileCategory || '').toUpperCase() === 'LOGO'
            );
            if (logoFile?.id) {
                const { previewUrl } = await fileAPI.getFilePreview(logoFile.id);
                setNetworkLogoUrl(previewUrl);
            }
        } catch (err) {
            console.error('Failed to load network logo:', err);
        } finally {
            setNetworkLogoLoading(false);
        }
    };

    const handleLogoUpload = async (e) => {
        const file = e?.target?.files?.[0];
        if (!file || !user?.id) return;
        e.target.value = '';
        if (!file.type.startsWith('image/')) {
            setAlert({ isOpen: true, title: 'Invalid file', message: 'Please select an image file (e.g. PNG, JPG).', type: 'error' });
            return;
        }
        setLogoUploading(true);
        try {
            const list = await fileAPI.getFilesByModule('INVESTOR_NETWORK', user.id);
            const existingLogos = (list || []).filter(
                (f) => (f.documentType || f.fileCategory || '').toUpperCase() === 'LOGO'
            );
            for (const f of existingLogos) {
                try {
                    await fileAPI.deleteFile(f.id);
                } catch (_) {}
            }
            const formData = new FormData();
            formData.append('file', file);
            formData.append('moduleType', 'INVESTOR_NETWORK');
            formData.append('moduleId', String(user.id));
            formData.append('fileCategory', 'LOGO');
            formData.append('visibility', 'PUBLIC');
            const res = await fetch(getApiUrl('files/upload'), {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
                body: formData
            });
            if (!res.ok) {
                const errData = await res.json().catch(() => ({}));
                throw new Error(errData.message || 'Upload failed');
            }
            await loadNetworkLogo();
            setAlert({ isOpen: true, title: 'Success', message: 'Logo updated successfully.', type: 'success' });
        } catch (err) {
            setAlert({ isOpen: true, title: 'Error', message: err.message || 'Failed to upload logo', type: 'error' });
        } finally {
            setLogoUploading(false);
        }
    };

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    useEffect(() => {
        if (user?.id) loadNetworkLogo();
        return () => {
            setNetworkLogoUrl((prev) => {
                if (prev && prev.startsWith('blob:')) window.URL.revokeObjectURL(prev);
                return null;
            });
        };
    }, [user?.id]);

    if (loading && !(isAdminMode && !effectiveNetworkId)) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-600 border-t-transparent mx-auto"></div>
                    <p className="mt-4 text-gray-600 font-medium">Loading your dashboard...</p>
                </div>
            </div>
        );
    }

    if (isAdminMode && !effectiveNetworkId && investorNetworks.length === 0) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-2 border-blue-600 border-t-transparent mx-auto" />
                    <p className="mt-4 text-gray-600 font-medium">Loading networks...</p>
                </div>
            </div>
        );
    }

    const navItems = [
        { id: 'portfolio', label: 'Portfolio', icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4' },
        { id: 'startups', label: 'Startups', icon: 'M13 10V3L4 14h7v7l9-11h-7z' },
        { id: 'applied-startups', label: 'Applied Startups', icon: 'M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2V9l-4-4H9zM9 3v6h6' },
        { id: 'investors', label: 'Investors', icon: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z' },
        { id: 'users', label: 'Management', icon: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z' },
        { id: 'documents', label: 'Documents', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
        { id: 'activity', label: 'Activity', icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z' }
    ];

    return (
        <div className="min-h-screen bg-slate-50/80">
            {/* Slim modern sidebar */}
            <aside
                className={`fixed left-0 top-0 h-full z-40 flex flex-col bg-white/95 backdrop-blur-sm border-r border-slate-200/80 shadow-sm transition-[width] duration-200 ease-out ${sidebarOpen ? 'w-52' : 'w-[72px]'}`}
            >
                {/* Logo row */}
                <div className="h-14 flex items-center shrink-0 px-3 border-b border-slate-100">
                    {sidebarOpen ? (
                        <Logo width={36} height={36} showText={true} className="[&_span]:!text-lg" />
                    ) : (
                        <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center">
                            <span className="text-white font-bold text-sm">i</span>
                        </div>
                    )}
                </div>

                {/* Nav */}
                <nav className="flex-1 py-3 px-2 space-y-0.5 overflow-y-auto">
                    {navItems.map((item) => (
                        <button
                            key={item.id}
                            onClick={() => (item.id === 'investors' ? navigate(`/${tenantPath}/investor-network/investors`) : navigateToTab(item.id))}
                            title={item.label}
                            className={`w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-all duration-150 ${activeTab === item.id
                                ? 'bg-indigo-50 text-indigo-700 font-medium shadow-sm'
                                : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                                }`}
                        >
                            <svg className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={item.icon} />
                            </svg>
                            {sidebarOpen && <span className="text-sm truncate">{item.label}</span>}
                        </button>
                    ))}
                </nav>

                {/* Expand/collapse pin */}
                <div className="shrink-0 px-2 py-1 border-t border-slate-100">
                    <button
                        type="button"
                        onClick={() => setSidebarExpanded((e) => !e)}
                        className="w-full flex items-center gap-3 rounded-lg px-3 py-2 text-slate-500 hover:bg-slate-50 hover:text-slate-700 transition-colors"
                        title={sidebarExpanded ? 'Collapse menu' : 'Expand menu'}
                    >
                        <svg className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={sidebarExpanded ? 'M11 19l-7-7 7-7m8 14l-7-7 7-7' : 'M13 5l7 7-7 7M5 5l7 7-7 7'} />
                        </svg>
                        {sidebarOpen && <span className="text-xs font-medium">{sidebarExpanded ? 'Collapse' : 'Expand'}</span>}
                    </button>
                </div>

                {/* Network + logo upload */}
                <div className="shrink-0 p-2 border-t border-slate-100 bg-slate-50/50">
                    <input ref={logoInputRef} type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} />
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={() => logoInputRef.current?.click()}
                            disabled={logoUploading || networkLogoLoading}
                            className="flex-shrink-0 w-9 h-9 rounded-lg overflow-hidden bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center border border-violet-200/50 hover:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-400 disabled:opacity-60 transition-all"
                            title={networkLogoUrl ? 'Change logo' : 'Upload logo'}
                        >
                            {logoUploading || networkLogoLoading ? (
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            ) : networkLogoUrl ? (
                                <img src={networkLogoUrl} alt="Network" className="w-full h-full object-cover" />
                            ) : (
                                <span className="text-white font-bold text-sm">
                                    {(user?.organizationName || user?.name || 'N')?.charAt(0)?.toUpperCase()}
                                </span>
                            )}
                        </button>
                        {sidebarOpen && (
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-slate-900 truncate">{user?.organizationName || user?.name}</p>
                                <p className="text-xs text-slate-500 truncate">{user?.email}</p>
                                <button
                                    type="button"
                                    onClick={() => logoInputRef.current?.click()}
                                    disabled={logoUploading || networkLogoLoading}
                                    className="text-xs text-violet-600 hover:text-violet-800 hover:underline disabled:opacity-60"
                                >
                                    {networkLogoUrl ? 'Change' : 'Upload'} logo
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </aside>

            {/* Main Content */}
            <main className={`transition-[margin] duration-200 ease-out ${sidebarOpen ? 'ml-52' : 'ml-[72px]'}`}>
                {/* Header */}
                <header className="h-14 bg-white/80 backdrop-blur-sm border-b border-slate-200/80 flex items-center justify-between px-6 sticky top-0 z-30">
                    <div className="flex items-center gap-4">
                        <h1 className="text-xl font-bold text-gray-900">
                            {activeTab === 'portfolio' && 'Portfolio Overview'}
                            {activeTab === 'startups' && 'Portfolio Companies'}
                            {activeTab === 'applied-startups' && 'Applied Startups'}
                            {activeTab === 'investors' && 'Investors'}
                            {activeTab === 'users' && 'Management Users'}
                            {activeTab === 'documents' && 'Documents'}
                            {activeTab === 'activity' && 'Recent Activity'}
                        </h1>
                        {isAdminMode && (
                            <select
                                value={selectedNetworkId || ''}
                                onChange={(e) => setSelectedNetworkId(e.target.value ? Number(e.target.value) : null)}
                                className="px-3 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                                <option value="">Select network</option>
                                {(investorNetworks || []).map((n) => (
                                    <option key={n.id} value={n.id}>{n.organizationName || n.name || n.email}</option>
                                ))}
                            </select>
                        )}
                    </div>
                    <div className="flex items-center space-x-4">
                        <button className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                            </svg>
                        </button>
                        <div className="relative" ref={profileMenuRef}>
                            <button
                                onClick={() => setShowProfileMenu(!showProfileMenu)}
                                className="flex items-center space-x-2 p-2 hover:bg-gray-100 rounded-lg transition"
                            >
                                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center">
                                    <span className="text-white text-sm font-bold">
                                        {user?.name?.charAt(0)?.toUpperCase()}
                                    </span>
                                </div>
                                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                                </svg>
                            </button>
                            {showProfileMenu && (
                                <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-200 py-2 z-50">
                                    <div className="px-4 py-3 border-b border-gray-100">
                                        <p className="text-sm font-medium text-gray-900">{user?.name}</p>
                                        <p className="text-xs text-gray-500">{user?.email}</p>
                                    </div>
                                    <button
                                        onClick={() => {
                                            setShowChangePasswordModal(true);
                                            setShowProfileMenu(false);
                                        }}
                                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                                        </svg>
                                        <span>Change password</span>
                                    </button>
                                    <button
                                        onClick={() => navigate(`/${user?.tenantIdentifier || 'default'}/my-subscription`)}
                                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                                        </svg>
                                        <span>Subscription</span>
                                    </button>
                                    <button
                                        onClick={() => navigate(`/${user?.tenantIdentifier || 'default'}/billing`)}
                                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                        </svg>
                                        <span>Billing</span>
                                    </button>
                                    <div className="border-t border-gray-100 mt-2 pt-2">
                                        <button
                                            onClick={handleLogout}
                                            className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center space-x-2"
                                        >
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                                            </svg>
                                            <span>Logout</span>
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </header>

                {/* Content */}
                <div className="p-8">
                    {/* Portfolio Tab */}
                    {activeTab === 'portfolio' && (
                        <div className="space-y-8">
                            {/* Stats Cards */}
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                <div className="bg-white rounded-2xl p-6 border border-gray-200 hover:shadow-lg transition-shadow">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">Total Invested</p>
                                            <p className="text-3xl font-bold text-gray-900 mt-2">₹{formatCurrency(stats.totalInvested)}</p>
                                        </div>
                                        <div className="w-14 h-14 bg-gradient-to-br from-green-400 to-emerald-500 rounded-2xl flex items-center justify-center shadow-lg shadow-green-500/30">
                                            <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-white rounded-2xl p-6 border border-gray-200 hover:shadow-lg transition-shadow">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">Portfolio Companies</p>
                                            <p className="text-3xl font-bold text-gray-900 mt-2">{stats.activeStartups}</p>
                                        </div>
                                        <div className="w-14 h-14 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-500/30">
                                            <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                            </svg>
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-white rounded-2xl p-6 border border-gray-200 hover:shadow-lg transition-shadow">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">Investors</p>
                                            <p className="text-3xl font-bold text-gray-900 mt-2">{stats.teamMembers}</p>
                                        </div>
                                        <div className="w-14 h-14 bg-gradient-to-br from-purple-400 to-pink-500 rounded-2xl flex items-center justify-center shadow-lg shadow-purple-500/30">
                                            <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                            </svg>
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-white rounded-2xl p-6 border border-gray-200 hover:shadow-lg transition-shadow">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">Management</p>
                                            <p className="text-3xl font-bold text-gray-900 mt-2">{stats.totalNetworkUsers}</p>
                                        </div>
                                        <div className="w-14 h-14 bg-gradient-to-br from-indigo-400 to-purple-500 rounded-2xl flex items-center justify-center shadow-lg shadow-indigo-500/30">
                                            <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                            </svg>
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-white rounded-2xl p-6 border border-gray-200 hover:shadow-lg transition-shadow">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">Documents</p>
                                            <p className="text-3xl font-bold text-gray-900 mt-2">{stats.totalDocuments || 0}</p>
                                        </div>
                                        <div className="w-14 h-14 bg-gradient-to-br from-cyan-400 to-teal-500 rounded-2xl flex items-center justify-center shadow-lg shadow-cyan-500/30">
                                            <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                            </svg>
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-white rounded-2xl p-6 border border-gray-200 hover:shadow-lg transition-shadow">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">Total Investments</p>
                                            <p className="text-3xl font-bold text-gray-900 mt-2">{stats.totalInvestments}</p>
                                        </div>
                                        <div className="w-14 h-14 bg-gradient-to-br from-orange-400 to-red-500 rounded-2xl flex items-center justify-center shadow-lg shadow-orange-500/30">
                                            <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                            </svg>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Portfolio Startups */}
                            <div>
                                <div className="flex items-center justify-between mb-6">
                                    <div>
                                        <h2 className="text-xl font-bold text-gray-900">Portfolio Companies</h2>
                                        <p className="text-sm text-gray-500 mt-1">Startups your network has invested in</p>
                                    </div>
                                    <button
                                        onClick={() => navigateToTab('startups')}
                                        className="px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg font-medium transition"
                                    >
                                        View All
                                    </button>
                                </div>

                                {startups.length === 0 ? (
                                    <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center">
                                        <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                                            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                            </svg>
                                        </div>
                                        <h3 className="text-lg font-semibold text-gray-900 mb-2">No portfolio companies yet</h3>
                                        <p className="text-gray-500 mb-6">Start investing in startups to build your portfolio</p>
                                        <button
                                            onClick={() => navigateToTab('startups')}
                                            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium"
                                        >
                                            Explore Startups
                                        </button>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                        {startups.slice(0, 6).map((startup) => {
                                            const investment = portfolioInvestmentForCard(
                                                startup.id,
                                                networkStartupTotals,
                                                investments,
                                                networkLinkedStartupIds,
                                            );
                                            return (
                                                <StartupPortfolioCard
                                                    key={startup.id}
                                                    startup={startup}
                                                    investment={investment}
                                                    showInvestmentDetails={true}
                                                />
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Startups Tab */}
                    {activeTab === 'startups' && (
                        <div className="space-y-6">
                            {/* Link startup by email or name */}
                            <div className="bg-white rounded-2xl border border-gray-200 p-6">
                                <h3 className="text-sm font-semibold text-gray-900 mb-2">Link a startup</h3>
                                <div className="flex flex-wrap items-center gap-3">
                                    <input
                                        type="text"
                                        value={startupSearchQuery}
                                        onChange={(e) => { setStartupSearchResult(null); setStartupSearchQuery(e.target.value); }}
                                        onKeyDown={(e) => e.key === 'Enter' && searchStartupByEmailOrName()}
                                        placeholder="Email or startup name"
                                        className="flex-1 min-w-[200px] px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                    <button
                                        type="button"
                                        onClick={searchStartupByEmailOrName}
                                        disabled={startupSearchLoading}
                                        className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition font-medium disabled:opacity-50 flex items-center space-x-2"
                                    >
                                        {startupSearchLoading ? (
                                            <>
                                                <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></span>
                                                <span>Searching...</span>
                                            </>
                                        ) : (
                                            <>
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                                </svg>
                                                <span>Search</span>
                                            </>
                                        )}
                                    </button>
                                </div>
                                {startupSearchResult && (
                                    <div className="mt-4 p-4 rounded-xl border border-gray-200 bg-gray-50">
                                        {!startupSearchResult.found ? (
                                            <div className="text-center py-2">
                                                <p className="text-amber-800 font-medium mb-1">Startup not found</p>
                                            </div>
                                        ) : (
                                            <div className="space-y-3">
                                                {startupSearchResult.startups.map((s) => (
                                                    <div key={s.id} className="flex flex-wrap items-center justify-between gap-4 p-3 rounded-lg bg-white border border-gray-100">
                                                        <div className="flex items-center space-x-3">
                                                            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white font-bold">
                                                                {(s.organizationName || s.name)?.charAt(0)?.toUpperCase() || '?'}
                                                            </div>
                                                            <div>
                                                                <p className="font-medium text-gray-900">{s.organizationName || s.name}</p>
                                                                <p className="text-sm text-gray-500">{s.email}</p>
                                                            </div>
                                                        </div>
                                                        {s.alreadyLinked ? (
                                                            <span className="text-green-700 font-medium text-sm">Already linked</span>
                                                        ) : (
                                                            <button
                                                                type="button"
                                                                onClick={() => openLinkStartupWithInvestmentsModal(s)}
                                                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium"
                                                            >
                                                                Link & add investments
                                                            </button>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {startups.length === 0 ? (
                                <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center">
                                    <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                                        <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                        </svg>
                                    </div>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">No portfolio companies yet</h3>
                                    <p className="text-gray-500 mb-2">Use search above to link a startup.</p>
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                    {startups.map((startup) => {
                                        const investment = portfolioInvestmentForCard(
                                            startup.id,
                                            networkStartupTotals,
                                            investments,
                                            networkLinkedStartupIds,
                                        );
                                        return (
                                            <StartupPortfolioCard
                                                key={startup.id}
                                                startup={startup}
                                                investment={investment}
                                                showInvestmentDetails={true}
                                            />
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}
                    {activeTab === 'applied-startups' && (
                        <div className="space-y-4">
                            <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center justify-between">
                                <div>
                                    <h3 className="text-base font-semibold text-gray-900">Startup request intake</h3>
                                    <p className="text-sm text-gray-500">Control whether startups can submit new investment requests.</p>
                                </div>
                                <label className="inline-flex items-center gap-2 text-sm font-medium text-gray-700">
                                    <input
                                        type="checkbox"
                                        checked={acceptStartupRequests}
                                        onChange={async (e) => {
                                            const next = e.target.checked;
                                            setAcceptStartupRequests(next);
                                            try {
                                                const res = await fetch(getApiUrl('users/my-network/profile'), {
                                                    method: 'PUT',
                                                    headers: {
                                                        'Content-Type': 'application/json',
                                                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                                                    },
                                                    body: JSON.stringify({ acceptStartupRequests: next })
                                                });
                                                if (!res.ok) throw new Error('Failed to save preference');
                                            } catch (error) {
                                                setAcceptStartupRequests(!next);
                                                setAlert({ isOpen: true, title: 'Error', message: 'Failed to update request intake preference.', type: 'error' });
                                            }
                                        }}
                                    />
                                    {acceptStartupRequests ? 'On' : 'Off'}
                                </label>
                            </div>
                            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                                <div className="px-4 py-3 border-b border-gray-200">
                                    <h3 className="text-base font-semibold text-gray-900">Applied startups</h3>
                                </div>
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-3 py-2 text-left text-xs text-gray-500 uppercase">Startup</th>
                                                <th className="px-3 py-2 text-left text-xs text-gray-500 uppercase">Pitch Deck</th>
                                                <th className="px-3 py-2 text-left text-xs text-gray-500 uppercase">Message</th>
                                                <th className="px-3 py-2 text-left text-xs text-gray-500 uppercase">Status</th>
                                                <th className="px-3 py-2 text-left text-xs text-gray-500 uppercase">Action</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100">
                                            {startupApplications.length === 0 ? (
                                                <tr>
                                                    <td className="px-3 py-6 text-sm text-gray-500" colSpan={5}>No startup applications yet.</td>
                                                </tr>
                                            ) : startupApplications.map((app) => (
                                                <tr key={app.id}>
                                                    <td className="px-3 py-2 text-sm text-gray-900">{app.startupName}</td>
                                                    <td className="px-3 py-2 text-sm text-gray-700">
                                                        <div className="flex items-center gap-2">
                                                            <span>{app.pitchDeckFileName}</span>
                                                            {app.pitchDeckFileId && (
                                                                <button
                                                                    type="button"
                                                                    className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                                                                    onClick={async () => {
                                                                        try {
                                                                            const preview = await fileAPI.getFilePreview(app.pitchDeckFileId);
                                                                            window.open(preview.previewUrl, '_blank', 'noopener,noreferrer');
                                                                        } catch (error) {
                                                                            setAlert({ isOpen: true, title: 'Error', message: 'Unable to open pitch deck.', type: 'error' });
                                                                        }
                                                                    }}
                                                                >
                                                                    View Document
                                                                </button>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="px-3 py-2 text-sm text-gray-700">{app.message || '—'}</td>
                                                    <td className="px-3 py-2 text-sm font-medium">{app.status}</td>
                                                    <td className="px-3 py-2 text-sm">
                                                        {app.status === 'PENDING' ? (
                                                            <div className="flex gap-2">
                                                                <button
                                                                    className="px-2 py-1 bg-emerald-600 text-white rounded"
                                                                    onClick={async () => {
                                                                        await startupInvestmentApplicationAPI.reviewApplication(app.id, 'APPROVED');
                                                                        await loadStartupApplications();
                                                                    }}
                                                                >
                                                                    Approve
                                                                </button>
                                                                <button
                                                                    className="px-2 py-1 bg-red-600 text-white rounded"
                                                                    onClick={async () => {
                                                                        await startupInvestmentApplicationAPI.reviewApplication(app.id, 'REJECTED');
                                                                        await loadStartupApplications();
                                                                    }}
                                                                >
                                                                    Reject
                                                                </button>
                                                            </div>
                                                        ) : 'Reviewed'}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Investors Tab — single header in app bar; content: toolbar + list */}
                    {activeTab === 'investors' && (
                        <div className="space-y-5">
                            {/* Toolbar: count + Add Investor + compact search */}
                            <div className="flex flex-wrap items-center justify-between gap-3">
                                <div className="flex items-center gap-3">
                                    <span className="text-sm text-gray-500">
                                        {investors.length} {investors.length === 1 ? 'member' : 'members'} in your network
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() => setInvestorSearchExpanded((e) => !e)}
                                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition ${investorSearchExpanded ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                        </svg>
                                        Add by email
                                    </button>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setShowAddInvestorModal(true)}
                                    className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition font-medium text-sm shadow-sm hover:shadow"
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                                    </svg>
                                    Add Investor
                                </button>
                            </div>

                            {/* Inline search (expandable) */}
                            {investorSearchExpanded && (
                                <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <input
                                            type="email"
                                            value={investorSearchEmail}
                                            onChange={(e) => { setInvestorSearchResult(null); setInvestorSearchEmail(e.target.value); }}
                                            onKeyDown={(e) => e.key === 'Enter' && searchInvestorByEmail()}
                                            placeholder="investor@example.com"
                                            className="flex-1 min-w-[200px] px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                        />
                                        <button
                                            type="button"
                                            onClick={searchInvestorByEmail}
                                            disabled={investorSearchLoading}
                                            className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 text-sm font-medium disabled:opacity-50 flex items-center gap-2"
                                        >
                                            {investorSearchLoading ? (
                                                <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                                            ) : (
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                                </svg>
                                            )}
                                            Search
                                        </button>
                                    </div>
                                    {investorSearchResult && (
                                        <div className="mt-3 p-3 rounded-lg bg-gray-50 border border-gray-100 text-sm">
                                            {!investorSearchResult.found ? (
                                                <div className="flex flex-wrap items-center justify-between gap-3">
                                                    <p className="text-gray-700">No user found. Create and link to network.</p>
                                                    <button
                                                        type="button"
                                                        onClick={() => { setPendingCreateEmail(investorSearchEmail); setShowAddInvestorModal(true); }}
                                                        className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-medium"
                                                    >
                                                        Create & link
                                                    </button>
                                                </div>
                                            ) : investorSearchResult.alreadyInNetwork ? (
                                                <p className="text-green-700 font-medium">Already in your network.</p>
                                            ) : !investorSearchResult.isInvestor ? (
                                                <p className="text-amber-700">User exists but is not an investor.</p>
                                            ) : (
                                                <div className="flex flex-wrap items-center justify-between gap-3">
                                                    <span className="font-medium text-gray-900">{investorSearchResult.user?.name}</span>
                                                    <button
                                                        type="button"
                                                        onClick={() => linkInvestorToNetwork(investorSearchResult.user.id)}
                                                        className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-medium"
                                                    >
                                                        Link to network
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}

                            {investors.length === 0 ? (
                                <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center shadow-sm">
                                    <div className="w-16 h-16 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                                        <svg className="w-8 h-8 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                                        </svg>
                                    </div>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">No investors yet</h3>
                                    <p className="text-gray-500 mb-6 max-w-sm mx-auto">Add investors to your network to collaborate on investments and track portfolios together.</p>
                                    <button
                                        type="button"
                                        onClick={() => setShowAddInvestorModal(true)}
                                        className="px-5 py-2.5 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition font-medium shadow-sm"
                                    >
                                        Add investor
                                    </button>
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {investors.map((investor) => {
                                        const investorInvestments = investments.filter((inv) => inv.investorId === investor.id);
                                        return (
                                            <button
                                                key={investor.id}
                                                type="button"
                                                onClick={() => setSelectedInvestorForStartups(investor)}
                                                className="group bg-white rounded-2xl border border-gray-200 p-5 hover:shadow-md hover:border-indigo-200 hover:shadow-indigo-500/5 transition-all duration-200 text-left cursor-pointer w-full"
                                            >
                                                <div className="flex items-start gap-4">
                                                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center flex-shrink-0 shadow-md group-hover:shadow-lg group-hover:scale-105 transition-transform">
                                                        <span className="text-white font-bold text-lg">
                                                            {investor.name?.charAt(0)?.toUpperCase()}
                                                        </span>
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <h3 className="font-semibold text-gray-900 truncate group-hover:text-indigo-600 transition-colors">{investor.name}</h3>
                                                        <p className="text-sm text-gray-500 truncate mt-0.5">{investor.email}</p>
                                                        {investor.jobTitle && (
                                                            <p className="text-xs text-gray-400 mt-1">{investor.jobTitle}</p>
                                                        )}
                                                        <div className="mt-2 flex flex-wrap items-center gap-2">
                                                            {investorInvestments.length > 0 && (
                                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-indigo-50 text-indigo-700">
                                                                    {investorInvestments.length} investment{investorInvestments.length !== 1 ? 's' : ''}
                                                                </span>
                                                            )}
                                                            {investor.networkRole && (
                                                                <span className={`inline-flex px-2 py-0.5 rounded-md text-xs font-medium ${investor.networkRole === 'CEO' ? 'bg-purple-50 text-purple-700' : investor.networkRole === 'ADMIN' ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
                                                                    {investor.networkRole}
                                                                </span>
                                                            )}
                                                        </div>
                                                        {investorInvestments.length > 0 && (
                                                            <p className="text-xs text-indigo-600 font-medium mt-1.5 opacity-0 group-hover:opacity-100 transition-opacity">View startups →</p>
                                                        )}
                                                    </div>
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Users Tab */}
                    {activeTab === 'users' && (
                        <div className="space-y-6">
                            <div className="flex items-center justify-between">
                                <p className="text-sm text-gray-500">Management staff in your network</p>
                                <button
                                    type="button"
                                    onClick={() => setShowAddUserModal(true)}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium flex items-center space-x-2 cursor-pointer"
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                                    </svg>
                                    <span>Add User</span>
                                </button>
                            </div>

                            {networkUsers.length === 0 ? (
                                <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center">
                                    <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                                        <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                        </svg>
                                    </div>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">No users yet</h3>
                                    <p className="text-gray-500 mb-6">Add management staff to your network</p>
                                    <button
                                        type="button"
                                        onClick={() => setShowAddUserModal(true)}
                                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium"
                                    >
                                        Add User
                                    </button>
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                    {networkUsers.map((networkUser) => (
                                        <div
                                            key={networkUser.id}
                                            className="bg-white rounded-2xl border border-gray-200 p-6 hover:shadow-lg transition-shadow cursor-default"
                                        >
                                            <div className="flex items-center space-x-4">
                                                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg">
                                                    <span className="text-white font-bold text-lg">
                                                        {networkUser.name?.charAt(0)?.toUpperCase()}
                                                    </span>
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <h3 className="font-semibold text-gray-900 truncate">{networkUser.name}</h3>
                                                    <p className="text-sm text-gray-500 truncate">{networkUser.email}</p>
                                                    {networkUser.jobTitle && (
                                                        <p className="text-xs text-gray-400 mt-1">{networkUser.jobTitle}</p>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
                                                <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${networkUser.networkRole === 'ADMIN' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'}`}>
                                                    {networkUser.networkRole || 'MANAGEMENT'}
                                                </span>
                                                <button
                                                    type="button"
                                                    onClick={() => setEditingNetworkUser(networkUser)}
                                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition"
                                                    title="Edit user"
                                                >
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                    </svg>
                                                    Edit
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Documents Tab */}
                    {activeTab === 'documents' && (
                        <div className="space-y-6">
                            <p className="text-sm text-gray-500">Upload and view files shared with your network</p>

                            {/* File Upload Section - same as admin page */}
                            <div className="bg-white rounded-2xl border border-gray-200 p-6">
                                <FileUpload
                                    onUploadSuccess={(fileName) => {
                                        loadFiles();
                                        setAlert({
                                            isOpen: true,
                                            title: 'Success',
                                            message: `File "${fileName}" uploaded successfully!`,
                                            type: 'success'
                                        });
                                    }}
                                    onUploadError={(error) => {
                                        setAlert({
                                            isOpen: true,
                                            title: 'Error',
                                            message: error || 'Failed to upload file',
                                            type: 'error'
                                        });
                                    }}
                                />
                            </div>

                            {files.length === 0 ? (
                                <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center">
                                    <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                                        <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                        </svg>
                                    </div>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">No documents yet</h3>
                                    <p className="text-gray-500">Upload a document using the section above, or view files shared with your network here.</p>
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                    {files.map((file) => (
                                        <div key={file.id} className="group bg-white border border-gray-200 rounded-xl p-5 hover:shadow-lg hover:border-blue-300 transition-all duration-300">
                                            <div className="flex items-start justify-between mb-4">
                                                <div className="flex items-center flex-1 min-w-0">
                                                    <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-xl flex items-center justify-center mr-3 group-hover:from-blue-200 group-hover:to-indigo-200 transition-colors flex-shrink-0">
                                                        <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={getFileIcon(file.contentType)} />
                                                        </svg>
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <h3
                                                            className="text-sm font-semibold text-gray-900 truncate group-hover:text-blue-600 transition-colors cursor-help"
                                                            title={file.originalName}
                                                        >
                                                            {file.originalName}
                                                        </h3>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                                                <div className="flex items-center text-xs text-gray-500">
                                                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                    </svg>
                                                    {formatDateTime(file.uploadedAt)}
                                                </div>
                                                <div className="flex space-x-1">
                                                    <button
                                                        onClick={() => previewFile(file)}
                                                        className="p-2 text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-all"
                                                        title="Preview"
                                                    >
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                                        </svg>
                                                    </button>
                                                    <button
                                                        onClick={() => downloadFile(file)}
                                                        className="p-2 text-emerald-600 bg-emerald-50 hover:bg-emerald-100 rounded-lg transition-all"
                                                        title="Download"
                                                    >
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                                        </svg>
                                                    </button>
                                                    <button
                                                        onClick={() => getFileMetadata(file)}
                                                        className="p-2 text-gray-600 bg-gray-50 hover:bg-gray-100 rounded-lg transition-all"
                                                        title="Details"
                                                    >
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                        </svg>
                                                    </button>
                                                    {/* Trace Views Button */}
                                                    {(file.uploadedById === user?.id) && (
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setTraceFile({ id: file.id, name: file.originalName });
                                                                setShowTraceModal(true);
                                                            }}
                                                            className="p-2 text-orange-600 bg-orange-50 hover:bg-orange-100 rounded-lg transition-all"
                                                            title="Trace Views"
                                                        >
                                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                            </svg>
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Activity Tab */}
                    {activeTab === 'activity' && (
                        <div className="space-y-6">
                            <p className="text-sm text-gray-500">Latest updates from your network</p>
                            {(() => {
                                const items = [];

                                (Array.isArray(investors) ? investors : []).forEach((inv) => {
                                    if (inv?.createdAt) {
                                        items.push({
                                            id: `investor-${inv.id}`,
                                            at: inv.createdAt,
                                            type: 'INVESTOR_ADDED',
                                            title: 'Investor added',
                                            text: `${inv.name || inv.email || 'Investor'} joined your network`
                                        });
                                    }
                                });

                                (Array.isArray(networkUsers) ? networkUsers : []).forEach((u) => {
                                    if (u?.createdAt) {
                                        items.push({
                                            id: `user-${u.id}`,
                                            at: u.createdAt,
                                            type: 'USER_ADDED',
                                            title: 'Management user added',
                                            text: `${u.name || u.email || 'User'} was added to your network`
                                        });
                                    }
                                });

                                (Array.isArray(linkedStartupActivity) ? linkedStartupActivity : []).forEach((s) => {
                                    if (s?.linkedAt) {
                                        items.push({
                                            id: `startup-link-${s.startupId}-${s.linkedAt}`,
                                            at: s.linkedAt,
                                            type: 'STARTUP_LINKED',
                                            title: 'Startup linked',
                                            text: `${s.startupName || s.startupEmail || 'Startup'} was linked to your network`
                                        });
                                    }
                                });

                                (Array.isArray(investments) ? investments : []).forEach((inv) => {
                                    if (inv?.createdAt) {
                                        items.push({
                                            id: `investment-${inv.id}`,
                                            at: inv.createdAt,
                                            type: 'INVESTMENT_ADDED',
                                            title: 'Investment recorded',
                                            text: `${inv.investorName || 'Investor'} invested ${inv.investmentAmount ? `₹${formatCurrency(inv.investmentAmount)}` : ''} in ${inv.startupName || 'startup'}`
                                        });
                                    }
                                });

                                (Array.isArray(files) ? files : []).forEach((f) => {
                                    const ts = f?.uploadedAt || f?.createdAt;
                                    if (ts) {
                                        items.push({
                                            id: `file-${f.id}`,
                                            at: ts,
                                            type: 'FILE_UPLOADED',
                                            title: 'File uploaded',
                                            text: `${f.originalName || 'Document'} was uploaded`
                                        });
                                    }
                                });

                                items.sort((a, b) => new Date(b.at) - new Date(a.at));
                                const recent = items.slice(0, 30);

                                if (recent.length === 0) {
                                    return (
                                        <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center">
                                            <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                                                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                            </div>
                                            <h3 className="text-lg font-semibold text-gray-900 mb-2">No activity yet</h3>
                                            <p className="text-gray-500">Add investors, users, link startups, or upload files to see activity here.</p>
                                        </div>
                                    );
                                }

                                return (
                                    <div className="bg-white rounded-2xl border border-gray-200 divide-y divide-gray-100 overflow-hidden">
                                        {recent.map((item) => (
                                            <div key={item.id} className="px-5 py-4 flex items-start justify-between gap-4 hover:bg-gray-50/70 transition">
                                                <div className="min-w-0">
                                                    <p className="text-sm font-semibold text-gray-900">{item.title}</p>
                                                    <p className="text-sm text-gray-600 mt-0.5 truncate">{item.text}</p>
                                                </div>
                                                <span className="text-xs text-gray-500 whitespace-nowrap">{formatDateTime(item.at)}</span>
                                            </div>
                                        ))}
                                    </div>
                                );
                            })()}
                        </div>
                    )}
                </div>
            </main>

            {/* File Details Modal */}
            {showFileModal && selectedFile && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto shadow-2xl">
                        <div className="p-6 border-b border-gray-100">
                            <div className="flex justify-between items-center">
                                <h3 className="text-xl font-bold text-gray-900">Document Details</h3>
                                <button
                                    onClick={() => {
                                        setShowFileModal(false);
                                        setSelectedFile(null);
                                    }}
                                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all"
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        </div>

                        <div className="p-6 space-y-6">
                            <div className="flex items-center">
                                <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-xl flex items-center justify-center mr-4">
                                    <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={getFileIcon(selectedFile.contentType)} />
                                    </svg>
                                </div>
                                <div>
                                    <h4 className="text-lg font-semibold text-gray-900">{selectedFile.originalName}</h4>
                                    <p className="text-sm text-gray-500">
                                        {selectedFile.fileSize ? `${(selectedFile.fileSize / 1024 / 1024).toFixed(2)} MB` : 'Unknown size'}
                                    </p>
                                </div>
                            </div>

                            <div className="space-y-4">
                                <div className="bg-gray-50 rounded-xl p-4">
                                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Uploaded By</p>
                                    <p className="text-sm font-medium text-gray-900">{selectedFile.uploadedByName}</p>
                                </div>
                                <div className="bg-gray-50 rounded-xl p-4">
                                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Upload Date</p>
                                    <p className="text-sm font-medium text-gray-900">{formatDateTime(selectedFile.uploadedAt)}</p>
                                </div>
                                <div className="bg-gray-50 rounded-xl p-4">
                                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">File Type</p>
                                    <p className="text-sm font-medium text-gray-900">{selectedFile.contentType || 'Unknown'}</p>
                                </div>
                                {selectedFile.tags && selectedFile.tags.length > 0 && (
                                    <div className="bg-gray-50 rounded-xl p-4">
                                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Tags</p>
                                        <div className="flex flex-wrap gap-2">
                                            {selectedFile.tags.map((tag, index) => (
                                                <span key={index} className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                                                    {tag}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="flex space-x-3 pt-4">
                                <button
                                    onClick={() => {
                                        setShowFileModal(false);
                                        setSelectedFile(null);
                                    }}
                                    className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-700 font-medium rounded-xl hover:bg-gray-50 transition-colors"
                                >
                                    Close
                                </button>
                                <button
                                    onClick={() => {
                                        previewFile(selectedFile);
                                        setShowFileModal(false);
                                    }}
                                    className="flex-1 px-4 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-medium rounded-xl hover:shadow-lg transition-all"
                                >
                                    Preview
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <FileTraceModal
                isOpen={showTraceModal}
                onClose={() => {
                    setShowTraceModal(false);
                    setTraceFile(null);
                }}
                fileId={traceFile?.id}
                fileName={traceFile?.name}
            />

            {/* Add User Modal */}
            <CreateNetworkUserModal
                isOpen={showAddUserModal}
                onClose={() => setShowAddUserModal(false)}
                onSuccess={() => {
                    setShowAddUserModal(false);
                    loadDashboardData();
                    setAlert({
                        isOpen: true,
                        title: 'Success',
                        message: 'Management user added successfully!',
                        type: 'success'
                    });
                }}
                networkId={effectiveNetworkId ?? user?.id}
                useMyNetworkApi={isManagementUser}
            />

            {/* Edit User Modal */}
            <CreateNetworkUserModal
                isOpen={!!editingNetworkUser}
                isEdit={true}
                initialData={editingNetworkUser ? {
                    id: editingNetworkUser.id,
                    name: editingNetworkUser.name || '',
                    email: editingNetworkUser.email || '',
                    phoneNumber: editingNetworkUser.phoneNumber || '',
                    jobTitle: editingNetworkUser.jobTitle || '',
                    department: editingNetworkUser.department || '',
                    networkRole: editingNetworkUser.networkRole || 'MANAGEMENT',
                    canViewFiles: editingNetworkUser.canViewFiles,
                    canViewInvestments: editingNetworkUser.canViewInvestments,
                    canManageUsers: editingNetworkUser.canManageUsers,
                    canManageFiles: editingNetworkUser.canManageFiles
                } : null}
                onClose={() => setEditingNetworkUser(null)}
                onSuccess={() => {
                    setEditingNetworkUser(null);
                    loadDashboardData();
                    setAlert({
                        isOpen: true,
                        title: 'Success',
                        message: 'User updated successfully!',
                        type: 'success'
                    });
                }}
                networkId={effectiveNetworkId ?? user?.id}
                useMyNetworkApi={isManagementUser}
            />

            {/* Add Investor Modal - shown on dashboard instead of navigating to network detail */}
            <CreateInvestorModal
                isOpen={showAddInvestorModal}
                onClose={() => { setShowAddInvestorModal(false); setPendingCreateEmail(''); }}
                onSuccess={() => {
                    setShowAddInvestorModal(false);
                    setPendingCreateEmail('');
                    setInvestorSearchResult(null);
                    setInvestorSearchEmail('');
                    loadDashboardData();
                    setAlert({
                        isOpen: true,
                        title: 'Success',
                        message: 'Investor added successfully!',
                        type: 'success'
                    });
                }}
                initialType="INDIVIDUAL"
                initialData={{ investorNetworkId: user?.id, email: pendingCreateEmail || undefined }}
                disableNetworkType={true}
            />

            {/* Link startup with investments modal (Investor Network) */}
            {linkStartupWithInvestmentsModal && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-2 z-50">
                    <div className="bg-white rounded-2xl w-full max-w-[98vw] max-h-[95vh] overflow-hidden shadow-2xl flex flex-col">
                        <div className="p-4 border-b border-gray-200 flex-shrink-0 flex items-start justify-between gap-4">
                            <div>
                                <h3 className="text-lg font-bold text-gray-900">Link startup &amp; record investments</h3>
                                <p className="text-sm text-gray-500 mt-1 truncate">
                                    {linkStartupWithInvestmentsModal.startup.name || linkStartupWithInvestmentsModal.startup.email}
                                </p>
                            </div>
                            <div className="flex items-center gap-3 flex-shrink-0">
                                <div className="flex items-center gap-2">
                                    <label className="text-xs font-medium text-gray-600">Stage</label>
                                    <select value={linkInvestmentsForm.investmentStage} onChange={(e) => setLinkInvestmentsForm((f) => ({ ...f, investmentStage: e.target.value }))} className="px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
                                        <option value="SEED">Seed</option>
                                        <option value="SERIES_A">Series A</option>
                                        <option value="SERIES_B">Series B</option>
                                        <option value="SERIES_C">Series C</option>
                                        <option value="GROWTH">Growth</option>
                                    </select>
                                </div>
                                <div className="flex items-center gap-2">
                                    <label className="text-xs font-medium text-gray-600">Currency</label>
                                    <select value={linkInvestmentsForm.currency} onChange={(e) => setLinkInvestmentsForm((f) => ({ ...f, currency: e.target.value }))} className="px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
                                        <option value="INR">INR</option>
                                        <option value="USD">USD</option>
                                        <option value="EUR">EUR</option>
                                    </select>
                                </div>
                                <div className="flex items-center gap-2">
                                    <label className="text-xs font-medium text-gray-600">Type</label>
                                    <select value={linkInvestmentsForm.investmentType} onChange={(e) => setLinkInvestmentsForm((f) => ({ ...f, investmentType: e.target.value }))} className="px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
                                        <option value="EQUITY">Equity</option>
                                        <option value="CONVERTIBLE_NOTE">Conv. Note</option>
                                        <option value="SAFE">SAFE</option>
                                    </select>
                                </div>
                                <div className="flex items-center gap-2">
                                    <label className="text-xs font-medium text-gray-600">Total</label>
                                    <input type="number" min="0" step="1" placeholder="Total amount" value={linkInvestmentsForm.totalAmount} onChange={(e) => setLinkInvestmentsForm((f) => ({ ...f, totalAmount: e.target.value }))} className="w-36 px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
                                </div>
                            </div>
                        </div>
                        <div className="overflow-auto flex-1 p-4">
                            {investors.length === 0 ? (
                                <p className="text-sm text-amber-700 bg-amber-50 p-3 rounded-lg">Add investors to your network first, then link this startup.</p>
                            ) : (
                                <div className="space-y-4">
                                    <div className="rounded-xl border border-gray-200 bg-gray-50 p-3">
                                        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                                            <div>
                                                <p className="text-sm font-medium text-gray-900">Investment sections</p>
                                                <p className="text-xs text-gray-500">Use the tabs to enter one group of share details at a time.</p>
                                            </div>
                                            <InvestmentSectionTabs tabs={LINK_STARTUP_SECTION_TABS} activeTab={activeLinkStartupSection} onChange={setActiveLinkStartupSection} />
                                        </div>
                                    </div>
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full w-full text-xs border-collapse">
                                            <thead>
                                                <tr className="bg-gray-50">
                                                    <th className="sticky left-0 z-10 bg-gray-50 border border-gray-200 px-2 py-1.5 text-left font-semibold text-gray-700 min-w-[160px]">Investor</th>
                                                    {currentLinkStartupColumns.map((column) => (
                                                        <th key={column.label} className="border border-gray-200 px-2 py-1.5 text-right font-semibold text-gray-700 min-w-[150px]">
                                                            {column.label}
                                                        </th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr className="bg-blue-50">
                                                    <td className="sticky left-0 z-10 bg-blue-50 border border-gray-200 px-2 py-1.5">
                                                        <p className="font-medium text-gray-900 truncate">{user?.organizationName || user?.name || 'Network'}</p>
                                                        <p className="text-xs text-gray-400">Self investment</p>
                                                    </td>
                                                    {currentLinkStartupColumns.map((column) => (
                                                        <td key={column.label} className="border border-gray-200 px-1 py-1">
                                                            {column.placeholder ? (
                                                                <span className="text-gray-400 text-xs px-1">{column.placeholder}</span>
                                                            ) : (
                                                                <input
                                                                    type="number"
                                                                    min="0"
                                                                    step={column.step}
                                                                    value={linkInvestmentsForm[column.networkKey] ?? ''}
                                                                    onChange={(e) => setLinkInvestmentsForm((f) => ({ ...f, [column.networkKey]: e.target.value }))}
                                                                    className="w-full px-1.5 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 text-right"
                                                                />
                                                            )}
                                                        </td>
                                                    ))}
                                                </tr>
                                                {investors.map((inv) => (
                                                    <tr key={inv.id} className="hover:bg-gray-50">
                                                        <td className="sticky left-0 z-10 bg-white hover:bg-gray-50 border border-gray-200 px-2 py-1.5">
                                                            <p className="font-medium text-gray-900 truncate">{inv.name}</p>
                                                            <p className="text-xs text-gray-400 truncate">{inv.email}</p>
                                                        </td>
                                                        {currentLinkStartupColumns.map((column) => (
                                                            <td key={column.label} className="border border-gray-200 px-1 py-1">
                                                                {column.placeholder ? (
                                                                    <span className="text-gray-400 text-xs px-1">{column.placeholder}</span>
                                                                ) : (
                                                                    <input
                                                                        type="number"
                                                                        min="0"
                                                                        step={column.step}
                                                                        value={linkInvestmentsForm[column.stateKey][inv.id] ?? ''}
                                                                        onChange={(e) => setLinkInvestmentsForm((f) => ({
                                                                            ...f,
                                                                            [column.stateKey]: { ...f[column.stateKey], [inv.id]: e.target.value }
                                                                        }))}
                                                                        className="w-full px-1.5 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 text-right"
                                                                    />
                                                                )}
                                                            </td>
                                                        ))}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                            <div className="mt-3 p-3 rounded-lg bg-gray-50 border border-gray-200 text-sm flex gap-6">
                                <div className="flex items-center gap-2">
                                    <span className="text-gray-600">Allocated total:</span>
                                    <span className="font-semibold text-gray-900">
                                        {(Object.values(linkInvestmentsForm.amounts || {}).reduce((sum, a) => sum + (parseFloat(a) || 0), 0) + (parseFloat(linkInvestmentsForm.networkAmount) || 0)).toLocaleString('en-IN')}
                                    </span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="text-gray-600">Remaining:</span>
                                    <span className="font-semibold text-gray-900">
                                        {Math.max((parseFloat(linkInvestmentsForm.totalAmount) || 0) - (Object.values(linkInvestmentsForm.amounts || {}).reduce((sum, a) => sum + (parseFloat(a) || 0), 0) + (parseFloat(linkInvestmentsForm.networkAmount) || 0)), 0).toLocaleString('en-IN')}
                                    </span>
                                </div>
                            </div>
                            <label className="mt-3 inline-flex items-center gap-2 text-sm text-gray-700">
                                <input
                                    type="checkbox"
                                    checked={!!linkInvestmentsForm.notReadyToInvestYet}
                                    onChange={(e) => setLinkInvestmentsForm((f) => ({ ...f, notReadyToInvestYet: e.target.checked }))}
                                    className="rounded border-gray-300"
                                />
                                Some investors are not ready to invest yet (link startup now and record investments later)
                            </label>
                        </div>
                        <div className="p-4 border-t border-gray-200 flex justify-end gap-2 flex-shrink-0">
                            <button type="button" onClick={() => setLinkStartupWithInvestmentsModal(null)} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium">
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={linkStartupWithInvestmentsToNetwork}
                                disabled={linkStartupSubmitting || investors.length === 0}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50 flex items-center gap-2"
                            >
                                {linkStartupSubmitting ? (
                                    <>
                                        <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                                        Saving...
                                    </>
                                ) : (
                                    'Link & save investments'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Investor detail modal - basic info + startups invested */}
            {selectedInvestorForStartups && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-2xl max-w-lg w-full max-h-[85vh] overflow-hidden shadow-2xl flex flex-col">
                        <div className="p-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
                            <h3 className="text-lg font-bold text-gray-900">Investor details</h3>
                            <button
                                type="button"
                                onClick={() => setSelectedInvestorForStartups(null)}
                                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <div className="overflow-y-auto flex-1 p-4 space-y-4">
                            {/* Basic investor information */}
                            <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                                <div className="flex items-start gap-4">
                                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow flex-shrink-0">
                                        <span className="text-white font-bold text-lg">
                                            {selectedInvestorForStartups.name?.charAt(0)?.toUpperCase() || '?'}
                                        </span>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="font-semibold text-gray-900">{selectedInvestorForStartups.name}</p>
                                        {selectedInvestorForStartups.email && (
                                            <p className="text-sm text-gray-600 mt-0.5 flex items-center gap-1.5">
                                                <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                                </svg>
                                                <a href={`mailto:${selectedInvestorForStartups.email}`} className="text-blue-600 hover:underline truncate block">
                                                    {selectedInvestorForStartups.email}
                                                </a>
                                            </p>
                                        )}
                                        {selectedInvestorForStartups.jobTitle && (
                                            <p className="text-sm text-gray-500 mt-1">{selectedInvestorForStartups.jobTitle}</p>
                                        )}
                                        {selectedInvestorForStartups.phoneNumber && (
                                            <p className="text-sm text-gray-500 mt-0.5 flex items-center gap-1.5">
                                                <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                                </svg>
                                                {selectedInvestorForStartups.phoneNumber}
                                            </p>
                                        )}
                                        {selectedInvestorForStartups.networkRole && (
                                            <span className={`inline-flex mt-2 px-2.5 py-1 rounded-full text-xs font-medium ${selectedInvestorForStartups.networkRole === 'CEO' ? 'bg-purple-100 text-purple-700' : selectedInvestorForStartups.networkRole === 'ADMIN' ? 'bg-blue-100 text-blue-700' : 'bg-gray-200 text-gray-700'}`}>
                                                {selectedInvestorForStartups.networkRole}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Startups invested */}
                            <div>
                                <h4 className="text-sm font-semibold text-gray-900 mb-2">Startups invested</h4>
                                {(() => {
                                    const invList = investments.filter((inv) => inv.investorId === selectedInvestorForStartups.id);
                                    if (invList.length === 0) {
                                        return (
                                            <p className="text-gray-500 text-sm py-4">No investments yet for this investor.</p>
                                        );
                                    }
                                    return (
                                        <ul className="space-y-3">
                                            {invList.map((inv) => {
                                                const startup = startups.find((s) => s.id === inv.startupId);
                                                const name = inv.startupName || startup?.organizationName || startup?.name || 'Startup';
                                                return (
                                                    <li
                                                        key={inv.id}
                                                        className="flex items-center justify-between gap-4 p-3 rounded-xl border border-gray-200 hover:border-blue-200 hover:bg-blue-50/50 transition"
                                                    >
                                                        <div className="flex-1 min-w-0">
                                                            <p className="font-medium text-gray-900 truncate">{name}</p>
                                                            <p className="text-sm text-gray-500">
                                                                Invested: {inv.investmentAmount != null ? `₹${formatCurrency(inv.investmentAmount)}` : '—'}
                                                                {inv.status && (
                                                                    <span className="ml-2 text-xs text-gray-400">({inv.status})</span>
                                                                )}
                                                            </p>
                                                        </div>
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                setSelectedInvestorForStartups(null);
                                                                navigate(`/${tenantPath}/investor-network/startups/${inv.startupId}`);
                                                            }}
                                                            className="flex-shrink-0 px-3 py-1.5 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition"
                                                        >
                                                            View startup
                                                        </button>
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    );
                                })()}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <AlertDialog
                isOpen={alert.isOpen}
                onClose={() => setAlert({ ...alert, isOpen: false })}
                title={alert.title}
                message={alert.message}
                type={alert.type}
            />

            <ChangePasswordModal
                isOpen={showChangePasswordModal}
                onClose={() => setShowChangePasswordModal(false)}
                onSuccess={() =>
                    setAlert({
                        isOpen: true,
                        title: 'Success',
                        message: 'Password updated successfully.',
                        type: 'success'
                    })
                }
            />
        </div>
    );
};

export default InvestorNetworkDashboard;
